import React, { useEffect, useState } from 'react';
import Row from '@amzn/meridian/row';
import Textarea from '@amzn/meridian/textarea';
import Text from '@amzn/meridian/text';

import RadioButton from '@amzn/meridian/radio-button';
import Box from '@amzn/meridian/box';
import { styles } from './ReviewBar.styles';
import { FormattedMessage } from 'react-intl';
import { I18NContent } from '../../api/CoachingNotes/schema';

type ReviewBarDataItem = {
  comments: string;
  checkedValue: number | undefined;
};
type ReviewBarItem = {
  expert: I18NContent[];
  intermediate: I18NContent[];
  novice: I18NContent[];
};

interface ReviewBarProps {
  bulletPoints?: {
    expert: I18NContent[];
    intermediate: I18NContent[];
    novice: I18NContent[];
  };
  onChange: (args: ReviewBarDataItem) => void;
  heading: I18NContent;
  forceResetState?: number;
  placeholder?: string;
  data: ReviewBarDataItem | undefined;
  csaFields?: (...args: string[]) => { [key: string]: string };
  disabled?: boolean;
}

const ReviewBar = ({
  bulletPoints,
  onChange,
  heading,
  forceResetState,
  placeholder,
  data,
  csaFields = (...args: string[]) => ({}),
  disabled,
}: ReviewBarProps) => {
  const [checkedValue, setCheckedValue] = useState<number | undefined>(
    data?.checkedValue
  );
  const [comments, setComments] = useState(data?.comments ?? '');

  const handleChange = (dataType: string) => (data: any) => {
    switch (dataType) {
      case 'comments':
        setComments(data);
        break;
      case 'radio':
        setCheckedValue(data);
        break;
    }
  };

  useEffect(() => {
    handleChange('comments')(data?.comments);
    handleChange('radio')(data?.checkedValue);
  }, [data]);

  useEffect(() => {
    onChange({ checkedValue, comments });
  }, [checkedValue]);

  useEffect(() => {
    if (!data) {
      setComments('');
      setCheckedValue(undefined);
    }
  }, [forceResetState]);

  return (
    <Box>
      <Text type='b500' className={styles.reviewBarTitle}>
        <FormattedMessage
          defaultMessage={heading.defaultMessage}
          id={heading.translationID}
        />
      </Text>
      <div className={styles.radioButtonRow}>
        <div className={styles.expertColumn}>
          <RadioButton
            disabled={disabled}
            checked={checkedValue === 0 ? true : false}
            onChange={handleChange('radio')}
            name='expert'
            value={0}
            {...csaFields(
              'radio-button',
              'expert',
              `cs_perfportal_sds_review_form_expert`
            )}
          >
            <span className={styles.iconDescriptor}>
              <FormattedMessage
                id='cs_perfportal_sds_review_form_expert'
                defaultMessage={'Expert'}
              />
            </span>
          </RadioButton>
        </div>
        <div className={styles.intermediateColumn}>
          <RadioButton
            disabled={disabled}
            checked={checkedValue === 1 ? true : false}
            onChange={handleChange('radio')}
            name='intermediate'
            value={1}
            {...csaFields(
              'radio-button',
              'intermediate',
              `cs_perfportal_sds_review_form_intermediate`
            )}
          >
            <span className={styles.iconDescriptor}>
              <FormattedMessage
                id='cs_perfportal_sds_review_form_intermediate'
                defaultMessage={'Intermediate'}
              />
            </span>
          </RadioButton>
        </div>
        <div className={styles.noviceColumn}>
          <RadioButton
            disabled={disabled}
            checked={checkedValue === 2 ? true : false}
            onChange={handleChange('radio')}
            name='novice'
            value={2}
            {...csaFields(
              'radio-button',
              'novice',
              `cs_perfportal_sds_review_form_novice`
            )}
          >
            <span className={styles.iconDescriptor}>
              <FormattedMessage
                id='cs_perfportal_sds_review_form_novice'
                defaultMessage={'Novice'}
              />
            </span>
          </RadioButton>
        </div>
      </div>
      <div className={styles.radioButtonContainer}>
        <div className={styles.expertColumn}>
          <div className={styles.descriptorLeft}>
            {bulletPoints?.expert.map(point => (
              <div className={styles.descriptorListItem}>
                <FormattedMessage
                  id={point.translationID}
                  defaultMessage={point.defaultMessage}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.intermediateColumn}>
          <div className={styles.descriptorRight}>
            {bulletPoints?.intermediate.map(point => (
              <div className={styles.descriptorListItem}>
                <FormattedMessage
                  id={point.translationID}
                  defaultMessage={point.defaultMessage}
                />
              </div>
            ))}
          </div>
        </div>
        <div className={styles.noviceColumn}>
          <div className={styles.descriptorRight}>
            {bulletPoints?.novice.map(point => (
              <div className={styles.descriptorListItem}>
                <FormattedMessage
                  id={point.translationID}
                  defaultMessage={point.defaultMessage}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className={styles.textAreaContainer}>
        <Textarea
          disabled={disabled}
          value={comments}
          onChange={handleChange('comments')}
          onBlur={() => {
            onChange({ checkedValue, comments });
          }}
          placeholder={placeholder}
          label=''
          width='100%'
          error={false}
          helperText=''
        />
      </div>
    </Box>
  );
};

export default ReviewBar;
